import { useEffect, useRef, useState } from 'react'

import { Message } from './interface'
import { usePostStore } from './ChatComponents/store'
import { fetchAnalyseTweetsSummaryMessage } from './api'
import { TwitterPost } from './ChatComponents/store/types/twitterPost'
import { RelatedQuestionsType, WebSourceLink } from './ChatComponents/store/slices/post.slice'
import {
    ArxivSearchResults,
    DiscordSearchResults,
    HackerNewsSearchResults,
    RedditSearchResults,
    SearchResults,
    WikipediaSearchResults,
    YoutubeSearchResults,
} from './ChatComponents/store/types/searchResults'
import { EventSourceMessage } from '@microsoft/fetch-event-source'

export const useChat = () => {
    const {
        setSearch,
        setPosts,
        setLinks,
        setImages,
        setVideos,
        setRelatedQuestions,
        setRelatedSearches,
        setDiscordMessages,
        videos,
        images,
        questions,
        searchQueries,
        links,
        posts: twitterPosts,
        discordMessages,
    } = usePostStore()

    const [messages, setMessages] = useState<Message>()
    const [inputValue, setInputValue] = useState<string>('')
    const [tools, setTools] = useState<string[]>([])
    const [isTyping, setIsTyping] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const controller = useRef<AbortController>(new AbortController())
    const texts = useRef<{ role: string; text: string }[]>([])

    const handleSendMessage = (
        text: string,
        tools: string[],
        date: string,
        model: string,
        responseType: string,
    ) => {
        if (isLoading || !text) return

        controller.current.abort()
        controller.current = new AbortController()

        setSearch(text)

        setInputValue(text)
        setTools(tools)
        // setDateFilter(date);
        // setModel(model);
        // setResponseType(responseType);
        setIsLoading(true)
        setImages([])
        setVideos([])
        setRelatedQuestions([])
        setRelatedSearches([])
        setDiscordMessages([])
        // setGeneratedImages([]);

        texts.current = []
        setMessages({
            text: '',
            type: 'text-loading',
        })
    }

    useEffect(() => {
        const onopen = (res: Response) => {
            if (res.ok && res.status === 200) {
                setMessages({
                    text: '',
                    type: 'text',
                })
                setIsTyping(true)
            } else {
                setMessages({
                    text: "Can't generate text",
                    type: 'error',
                })
            }
        }

        const processTwitterPosts = (tweetsContent: TwitterPost[]) => {
            const posts: TwitterPost[] = tweetsContent.map(tweet => ({
                ...tweet,
                media: tweet.media,
                user: tweet.user,
            }))

            const links = posts.reduce<WebSourceLink[]>((acc, post) => {
                if (post.user?.username) {
                    const tweetLink = `https://twitter.com/${post.user.username}/status/${post.id}`
                    acc.push({
                        id: tweetLink,
                        link: tweetLink,
                        content: post.text,
                    })
                }

                return acc
            }, [])

            setLinks(links)
            setPosts(posts)
        }

        const processSearchResults = (searchResults: SearchResults) => {
            const links: WebSourceLink[] = []

            const { organic_results } = searchResults

            // if (searchResults?.answer_box) {
            //   setImages(searchResults.answer_box.images);
            // }
            if (searchResults?.inline_videos) {
                setVideos(searchResults.inline_videos)
            }
            if (searchResults?.related_searches) {
                const queries = searchResults.related_searches.map(
                    (searches: any) => searches.query,
                )
                setRelatedSearches(queries)
            }
            if (searchResults?.related_questions) {
                const questions = searchResults.related_questions.map(
                    (question: RelatedQuestionsType) => {
                        return {
                            title: question.title,
                            link: question.link,
                            question: question.question,
                            snippet: question.snippet,
                        }
                    },
                )

                setRelatedQuestions(questions)
            }

            organic_results.forEach(({ link, snippet, title }) => {
                links.push({
                    id: link,
                    link,
                    content: snippet,
                    title,
                })
            })

            setLinks(links)
        }

        const processSearchImages = (searchResults: SearchResults) => {
            setImages(
                searchResults.images_results
                    ?.map((image: any) => {
                        return image.original
                    })
                    ?.slice(0, 5),
            )
        }

        const processRedditSearch = (searchResults: RedditSearchResults) => {
            const redditLinks: WebSourceLink[] = []
            searchResults.forEach?.(({ url, snippet }) => {
                redditLinks.push({
                    id: url,
                    link: url,
                    content: snippet,
                })
            })
            setLinks(redditLinks)
        }

        const processYoutubeSearch = (searchResult: YoutubeSearchResults) => {
            const youtubeLinks: WebSourceLink[] = []
            searchResult.forEach?.(({ url_suffix, title }) => {
                youtubeLinks.push({
                    id: url_suffix,
                    link: `https://www.youtube.com${url_suffix}`,
                    content: title,
                })
            })
            setLinks(youtubeLinks)
        }

        const processDiscordSearch = (searchResult: any) => {
            // console.log("searchResult", searchResult);
            const discordLinks: WebSourceLink[] = []
            // searchResult.body.forEach?.(({ link, content }) => {
            //   discordLinks.push({
            //     id: link,
            //     link,
            //     content,
            //   });
            // });

            setDiscordMessages(searchResult?.body)
            setLinks(discordLinks)
        }

        const processWikipediaSearch = (searchResult: WikipediaSearchResults) => {
            const wikiLinks: WebSourceLink[] = []
            searchResult.forEach?.(({ url, summary }) => {
                wikiLinks.push({
                    id: url,
                    link: url,
                    content: summary,
                })
            })
            setLinks(wikiLinks)
        }

        const processArxivSearch = (searchResult: ArxivSearchResults) => {
            const arxivLinks: WebSourceLink[] = []
            searchResult.forEach?.(({ arxiv_url, title }) => {
                arxivLinks.push({
                    id: arxiv_url,
                    link: arxiv_url,
                    content: title,
                })
            })
            setLinks(arxivLinks)
        }

        const processHackerNewsSearch = (searchResult: HackerNewsSearchResults) => {
            const hackerNewsLinks: WebSourceLink[] = []
            searchResult.forEach?.(({ url, snippet }) => {
                hackerNewsLinks.push({
                    id: url,
                    link: url,
                    content: snippet,
                })
            })
            setLinks(hackerNewsLinks)
        }

        const onmessage = (event: EventSourceMessage) => {
            if (event.data) {
                setIsLoading(false)
            }
            if (isJsonString(event.data)) {
                const data: {
                    type: string
                    content: unknown
                    role?: string
                    error?: string
                } = JSON.parse(event.data)

                if (data.error) {
                    throw new Error(`Error: ${data.error}`)
                }

                console.log('data type', data.type)

                if (data.type === 'text') {
                    const role = data.role as string
                    const textItem = texts.current.find(text => text.role === role)
                    if (textItem) {
                        textItem.text += data.content as string
                    } else {
                        texts.current.push({ role, text: data.content as string })
                    }
                    const text = texts.current.reduce((acc, text) => acc + text.text, '')

                    setMessages(message => (message ? { ...message, text } : message))
                } else if (data.type === 'tweets') {
                    processTwitterPosts(data.content as TwitterPost[])
                } else if (data.type === 'search') {
                    processSearchResults(data.content as SearchResults)
                } else if (data.type === 'google_image_search') {
                    processSearchImages(data.content as SearchResults)
                } else if (data.type === 'reddit_search') {
                    processRedditSearch(data.content as RedditSearchResults)
                } else if (data.type === 'youtube_search') {
                    processYoutubeSearch(data.content as YoutubeSearchResults)
                } else if (data.type === 'discord_search') {
                    processDiscordSearch(data.content as DiscordSearchResults)
                } else if (data.type === 'wikipedia_search') {
                    processWikipediaSearch(data.content as WikipediaSearchResults)
                } else if (data.type === 'arxiv_search') {
                    processArxivSearch(data.content as ArxivSearchResults)
                } else if (data.type === 'hacker_news_search') {
                    processHackerNewsSearch(data.content as HackerNewsSearchResults)
                }
            }
        }

        const onerror = (err: any) => {
            console.error(err)
            setMessages({
                text: 'Error during text generation.',
                type: 'error',
            })

            setIsLoading(false)
            throw new Error()
        }

        const onclose = () => {
            setIsLoading(false)
            setIsTyping(false)
        }

        if (inputValue && messages?.type === 'text-loading') {
            const messagesToSend = [
                {
                    role: 'user',
                    content: inputValue,
                    tools,
                    date_filter: 'PAST_2_WEEKS',
                    response_order: 'SUMMARY_FIRST',
                },
            ]

            fetchAnalyseTweetsSummaryMessage(
                messagesToSend,
                onopen,
                onmessage,
                onerror,
                onclose,
                controller.current.signal,
            ).catch(() => setIsTyping(false))
        }
    }, [inputValue, messages, tools])

    return {
        isLoading,
        messages,
        handleSendMessage,
        inputValue,
        setInputValue,
        twitterPosts,
        posts: links,
    }
}

export function isJsonString(str: string): boolean {
    try {
        JSON.parse(str)
    } catch (e) {
        return false
    }
    return true
}
